import { isCalendarDay } from "@hakili/contracts";
import { wholePercent } from "@hakili/domain-types";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import { BANK_FILE_STAGE_LABELS } from "@hakili/domain-types";
import { BANK_FS_BODY, BANK_FS_META, Callout, Chip, ProgressRing } from "@hakili/ui";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import type { ReactElement } from "react";

import { type CommitteeRead, committeeReadFrom } from "./comite.js";
import { type DecisionTakenView, decisionTakenFrom, Row, when } from "./decisions.js";
import { DASH, fill } from "./dossiers.js";

/**
 * Round `L-1b`, `M4` (c) and `M5` (d) , **the analyst's two panels to the
 * drawing** (`hakili-bank.jsx`, `ValidationPanel` and `OfferPanel`;
 * WF-BNK-10, WF-BNK-11; `P1-BKW-011`, `P1-BKW-012`).
 *
 * At `validation` the panel shows what is being sanctioned: the terms with
 * the gap chip, the two rate bars (the grid's a dash while no grade), the
 * interest effect, the justification with its timestamp, the comparables a
 * dash; the right column with the validation chain, the traceability and the
 * delegation statement from the configured threshold , never a hard-coded
 * figure. Once the chef has decided, the additive banner « Décision du chef
 * du crédit : Validée | Refusée | Renvoyée à l'instant » with the motive. The
 * drawing's simulated « Retour du chef du crédit » bar is its demonstration
 * device and is not built: the decision is the chef's, in his own screen.
 *
 * At `offre` the panel is the drawing's OfferPanel: the status line, the
 * « Offre transmise » card with the fees on two lines never merged, the
 * activity and questions cards with their empty sentences (dashes, never a
 * number), the competitive context with the count of banks the dossier
 * reached, the timetable, and the delegation circuit read-only; « Relancer
 * via Hakili », « Réviser les conditions », « Retirer l'offre » greyed with
 * their rounds, the client's acts (`L-2`) named as his.
 */
export interface OfferPanelView {
  readonly durationMonths: number;
  readonly nominalRatePct: string;
  readonly financesBankFee: boolean;
  readonly financesHakiliFee: boolean;
  readonly justification: string;
  readonly submittedAt: string | null;
  readonly recap: {
    readonly totalFinancedAmount: number;
    readonly instalmentAmount: number;
    readonly hakiliFeeAmount: number;
    readonly bankFeeAmount: number;
    readonly taegPct: string;
    readonly gridRatePct: string | null;
    readonly capacityAmount: number;
  };
  readonly gaps: readonly {
    readonly code: string;
    readonly rule: string;
    readonly detail: string;
    readonly level: string;
  }[];
  readonly gridComparable: boolean;
  /** Position 59: the words of a composition made before the submission's analysis ended, or `null`. */
  readonly gradeNote?: string | null;
}

export interface FileOfferRead {
  readonly stage: string;
  readonly offer: OfferPanelView | null;
  readonly decision: DecisionTakenView | null;
  readonly instructions: readonly { readonly id: string; readonly text: string }[];
  readonly analystName: string | null;
  readonly requiresCommittee: boolean;
  readonly facts: {
    readonly amountFinanced: number;
    readonly depositPct: number | null;
    readonly delegationThresholdAmount: number | null;
    readonly taegCeilingPct: string | null;
  };
  readonly sent: { readonly transmittedAt: string | null; readonly validUntil: string | null };
  /** `COMITE-RECORD-3` (frames 43 and 44): the reasons kept at the acceptance and the sittings recorded; `null` for a file that never sat before the committee. */
  readonly committee?: CommitteeRead | null | undefined;
  /** `C-5`: the door's own day (`YYYY-MM-DD`), for the validity ring; `null` when the door sends none. */
  readonly asOf?: string | null | undefined;
  /**
   * Round `L-2`, `M3` (F5): the client's answer by his own act , retained on
   * this offer with the circuit the file is on, or not retained; `null` while
   * he has not answered. Never the winner, never a competing offer's detail.
   */
  readonly clientAnswer: {
    readonly kind: "retenue" | "nonRetenue" | "declinee" | "expiree";
    readonly at: string;
    readonly circuit: "comite" | "signature" | null;
  } | null;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);

export function fileOfferFrom(answer: unknown): FileOfferRead | null {
  const body = asRecord(answer);
  if (body === null) return null;
  const facts = asRecord(body["facts"]);
  const policy = asRecord(facts?.["policy"]);
  const offer = asRecord(body["offer"]);
  const recap = asRecord(offer?.["recap"]);
  const gaps = Array.isArray(offer?.["gaps"])
    ? offer["gaps"]
        .map(asRecord)
        .filter((one): one is Record<string, unknown> => one !== null)
        .map((one) => ({
          code: str(one["code"]) ?? "",
          rule: str(one["rule"]) ?? "",
          detail: str(one["detail"]) ?? "",
          level: str(one["level"]) ?? "",
        }))
    : [];
  return {
    stage: str(body["stage"]) ?? "",
    offer:
      offer === null || recap === null
        ? null
        : {
            durationMonths: num(offer["durationMonths"]) ?? 0,
            nominalRatePct: str(offer["nominalRatePct"]) ?? "",
            financesBankFee: offer["financesBankFee"] === true,
            financesHakiliFee: offer["financesHakiliFee"] === true,
            justification: str(offer["justification"]) ?? "",
            submittedAt: str(offer["submittedAt"]),
            recap: {
              totalFinancedAmount: num(recap["totalFinancedAmount"]) ?? 0,
              instalmentAmount: num(recap["instalmentAmount"]) ?? 0,
              hakiliFeeAmount: num(recap["hakiliFeeAmount"]) ?? 0,
              bankFeeAmount: num(recap["bankFeeAmount"]) ?? 0,
              taegPct: str(recap["taegPct"]) ?? "",
              gridRatePct: str(recap["gridRatePct"]),
              capacityAmount: num(recap["capacityAmount"]) ?? 0,
            },
            gaps,
            gridComparable: offer["gridComparable"] === true,
            gradeNote: str(offer["gradeNote"]),
          },
    decision: decisionTakenFrom(body["decision"]),
    instructions: Array.isArray(body["instructions"])
      ? body["instructions"]
          .map(asRecord)
          .filter((one): one is Record<string, unknown> => one !== null)
          .map((one) => ({ id: str(one["id"]) ?? "", text: str(one["text"]) ?? "" }))
      : [],
    analystName: str(body["analystName"]),
    requiresCommittee: body["requiresCommittee"] === true,
    facts: {
      amountFinanced: num(facts?.["amountFinanced"]) ?? 0,
      depositPct: num(facts?.["depositPct"]),
      delegationThresholdAmount: num(policy?.["delegationThresholdAmount"]),
      taegCeilingPct: str(policy?.["taegCeilingPct"]),
    },
    sent: {
      transmittedAt: str(body["transmittedAt"]) ?? str(offer?.["transmittedAt"]),
      validUntil: str(body["validUntil"]) ?? str(offer?.["validUntil"]),
    },
    clientAnswer: clientAnswerFrom(body["clientAnswer"]),
    committee: committeeReadFrom(body["committee"]),
    // A1: a calendar day or nothing , a timestamp or a day that does not exist draws no ring, never a guessed one.
    asOf: isCalendarDay(body["asOf"]) ? body["asOf"] : null,
  };
}

const clientAnswerFrom = (raw: unknown): FileOfferRead["clientAnswer"] => {
  const one = asRecord(raw);
  if (one === null) return null;
  const kind = one["kind"];
  const at = str(one["at"]);
  if (
    (kind !== "retenue" && kind !== "nonRetenue" && kind !== "declinee" && kind !== "expiree") ||
    at === null
  )
    return null;
  const circuit = one["circuit"];
  return { kind, at, circuit: circuit === "comite" || circuit === "signature" ? circuit : null };
};

const rateLabel = (rate: string): string => rate.replace(".", ",");
const pct = (rate: string): string => fill("bank.decision.pct", { n: rateLabel(rate) });

/** WF-BNK-10 step 2 and WF-BNK-11 step 5: the delegation's sentence from the CONFIGURED threshold, or a dash. */
export function delegationSentence(amount: number, threshold: number | null, committee: boolean): string {
  if (threshold === null) return t("bank.validation.delegationInconnue");
  return committee
    ? fill("bank.validation.delegationComite", {
        montant: formatMoney(amount),
        seuil: formatMoney(threshold),
      })
    : fill("bank.validation.delegationDirecte", {
        montant: formatMoney(amount),
        seuil: formatMoney(threshold),
      });
}

const BANNER = new Map<
  DecisionTakenView["outcome"],
  { readonly key: string; readonly tone: "ok" | "bad" | "warn" }
>([
  ["validee", { key: "bank.validation.decisionValidee", tone: "ok" }],
  ["refusee", { key: "bank.validation.decisionRefusee", tone: "bad" }],
  ["renvoyee", { key: "bank.validation.decisionRenvoyee", tone: "warn" }],
]);

/** WF-BNK-10 step 3: the additive banner, once the chef has decided. */
export function DecisionBanner({ decision }: { readonly decision: DecisionTakenView }): ReactElement {
  const shape = BANNER.get(decision.outcome);
  return (
    <div className="mb-4">
      <Callout
        tone={shape?.tone ?? "ok"}
        testId="bank-validation-bandeau"
        icon={
          decision.outcome === "validee" ? (
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          ) : (
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          )
        }
      >
        <p className="font-semibold">
          {fill("bank.validation.decisionBandeau", {
            decision: t(shape?.key ?? "bank.validation.decisionValidee"),
            quand: when(decision.decidedAt),
          })}
        </p>
        {decision.note !== null ? (
          <p className="mt-0.5">{fill("bank.validation.motif", { motif: decision.note })}</p>
        ) : null}
      </Callout>
    </div>
  );
}

export function ValidationPanel(props: { readonly read: FileOfferRead }): ReactElement {
  const { read } = props;
  const offer = read.offer;
  const threshold = read.facts.delegationThresholdAmount;
  const committee = read.requiresCommittee;
  const chain = [
    {
      name: read.analystName ?? DASH,
      role: t("bank.validation.roleAnalyste"),
      state: "done" as const,
      detail:
        offer?.submittedAt === null || offer === null
          ? t("bank.validation.soumise")
          : fill("bank.validation.soumiseA", { quand: when(offer.submittedAt) }),
    },
    {
      name: t("bank.validation.chefDuCredit"),
      role: t("bank.validation.roleChef"),
      state: read.decision === null ? ("pending" as const) : ("done" as const),
      detail:
        threshold === null
          ? DASH
          : fill("bank.validation.delegationJusqua", { seuil: formatMoney(threshold) }),
    },
    {
      name: t("bank.validation.comite"),
      role: t("bank.validation.roleComite"),
      state: "later" as const,
      detail: committee ? t("bank.validation.comiteApres") : t("bank.validation.comiteNonRequis"),
    },
  ];
  return (
    <div data-testid="bank-validation">
      {read.decision === null ? null : <DecisionBanner decision={read.decision} />}
      <div className="mb-4">
        <p className="text-sm font-semibold text-gray-900">{t("bank.validation.enAttente")}</p>
        <p className="mt-0.5 text-sm text-gray-500" data-testid="bank-validation-soumis">
          {offer?.submittedAt === null || offer === null
            ? t("bank.validation.objectifDecision")
            : fill("bank.validation.soumisLeObjectif", {
                quand: when(offer.submittedAt),
                objectif: t("bank.validation.objectifDecision"),
              })}
        </p>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-4">
          <div className="overflow-hidden rounded-2xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h3 className="text-sm font-semibold">{t("bank.validation.conditionsSoumises")}</h3>
              <span data-testid="bank-validation-chip" data-kind="chip-label">
                <Chip tone={(offer?.gaps.length ?? 0) > 0 ? "alerte" : "ok"}>
                  {offer === null
                    ? DASH
                    : offer.gaps.length === 0
                      ? t("bank.validation.sansEcart")
                      : fill(offer.gaps.length > 1 ? "bank.decision.ecartsPlural" : "bank.decision.ecarts", {
                          n: String(offer.gaps.length),
                        })}
                </Chip>
              </span>
            </div>
            {offer === null ? (
              <p className="px-5 py-4 text-sm text-gray-500">{DASH}</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-x-8 px-5 py-4" data-testid="bank-validation-conditions">
                  <div>
                    <Row
                      label={t("bank.decision.montant")}
                      value={formatMoney(offer.recap.totalFinancedAmount)}
                      strong
                    />
                    <Row
                      label={t("bank.decision.duree")}
                      value={fill("bank.composer.mois", { n: String(offer.durationMonths) })}
                    />
                    <Row
                      label={t("bank.decision.mensualite")}
                      value={formatMoney(offer.recap.instalmentAmount)}
                    />
                  </div>
                  <div>
                    <Row label={t("bank.decision.tauxPropose")} value={pct(offer.nominalRatePct)} strong />
                    <Row
                      label={t("bank.validation.grilleBanque")}
                      value={offer.recap.gridRatePct === null ? DASH : pct(offer.recap.gridRatePct)}
                      testId="bank-validation-grille"
                    />
                    <Row
                      label={t("bank.composer.taeg")}
                      value={fill("bank.validation.taegPlafond", {
                        taeg: rateLabel(offer.recap.taegPct),
                        plafond:
                          read.facts.taegCeilingPct === null ? DASH : rateLabel(read.facts.taegCeilingPct),
                      })}
                    />
                  </div>
                </div>
                <div className="px-5 pb-4">
                  <RateBar
                    label={t("bank.validation.barreGrille")}
                    rate={offer.recap.gridRatePct}
                    tone="bg-gray-300"
                    testId="bank-validation-barre-grille"
                  />
                  <RateBar
                    label={t("bank.validation.barreProposee")}
                    rate={offer.nominalRatePct}
                    tone="bg-indigo-500"
                    strong
                    testId="bank-validation-barre-proposee"
                  />
                  {offer.gridComparable ? null : (
                    <p className="mt-3 text-xs text-gray-500" data-testid="bank-validation-effet">
                      {offer.gradeNote ?? t("bank.composer.grilleNonComparable")}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <div
            className="rounded-2xl border border-gray-200 px-5 py-4"
            data-testid="bank-validation-justification"
          >
            <h3 className="mb-2 text-sm font-semibold">{t("bank.decision.justificationTitre")}</h3>
            <p className="text-sm leading-relaxed text-gray-700">
              {offer === null || offer.justification === ""
                ? t("bank.decision.justificationAbsente")
                : offer.justification}
            </p>
            <p className={`mt-2 ${BANK_FS_META} text-gray-500`} data-meta>
              {read.analystName ?? DASH}
              {offer?.submittedAt === null || offer === null ? "" : `, ${when(offer.submittedAt)}`}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-200 px-5 py-4" data-testid="bank-validation-circuit">
            <h3 className="mb-3 text-sm font-semibold">{t("bank.validation.circuit")}</h3>
            <ul className="space-y-3">
              {chain.map((one) => (
                <li key={one.role} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    {one.state === "done" ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-hidden="true" />
                    ) : one.state === "pending" ? (
                      <Clock className="h-4 w-4 text-amber-500" aria-hidden="true" />
                    ) : (
                      <span className="h-4 w-4 rounded-full border-2 border-gray-200" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className={
                        "text-sm font-medium " + (one.state === "later" ? "text-gray-400" : "text-gray-900")
                      }
                    >
                      {one.name}
                    </p>
                    <p className="text-sm text-gray-500">{one.role}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-gray-400">{one.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="rounded-2xl border border-gray-200 px-5 py-4"
            data-testid="bank-validation-tracabilite"
          >
            <h3 className="mb-2 text-sm font-semibold">{t("bank.validation.tracabilite")}</h3>
            <p className="text-sm text-gray-700" data-testid="bank-validation-revue-terminee">
              {t("bank.validation.revueTerminee")}
            </p>
          </div>

          <div
            className="rounded-2xl border border-gray-200 px-5 py-4"
            data-testid="bank-validation-delegation"
          >
            <h3 className="mb-2 text-sm font-semibold">{t("bank.validation.delegation")}</h3>
            <p className="text-sm leading-relaxed text-gray-700">
              {delegationSentence(read.facts.amountFinanced, threshold, committee)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const RATE_SCALE_PCT = 14;

function RateBar(props: {
  readonly label: string;
  readonly rate: string | null;
  readonly tone: string;
  readonly strong?: boolean;
  readonly testId: string;
}): ReactElement {
  const value = props.rate === null ? null : Number(props.rate);
  const width = value === null ? 0 : (value / RATE_SCALE_PCT) * 100;
  return (
    <div className="mb-2 flex items-center gap-3" data-testid={props.testId}>
      <span className={`w-32 shrink-0 ${BANK_FS_META} text-gray-500`}>{props.label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${props.tone}`} style={{ width: `${String(width)}%` }} />
      </div>
      <span
        className={
          `w-14 text-right ${BANK_FS_META} tabular-nums ` +
          (props.strong === true ? "font-semibold text-indigo-600" : "text-gray-600")
        }
      >
        {props.rate === null ? DASH : pct(props.rate)}
      </span>
    </div>
  );
}

const DAY_MS = 86_400_000;
/** Whole days between two calendar days written `YYYY-MM-DD` (both read at midnight UTC: an exact integer, nothing rounded). */
const daysBetween = (from: string, to: string): number =>
  (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS;

/**
 * Round `C-5`: the validity as a ring that draws the time that REMAINS (D-1023-4: no record says what the ring draws; the
 * mockup's chip « Expire dans 5 jours » counts what remains, and the designer reads it so). Full on the day the offer was
 * sent, empty at the expiry; the label « N j » is the whole days left and the value is the same share as a whole percentage,
 * so label and value agree. Read at the DOOR's day (`asOf`, its own clock): the screen never reads a clock of its own, and
 * whether the offer is still valid stays the server's state. `null` when any of the three days is missing: nothing is
 * measured, so nothing is drawn.
 */
export function validityOf(
  sentDay: string | null,
  validUntil: string | null,
  asOf: string | null | undefined,
): { readonly leftPct: number; readonly daysLeft: number } | null {
  if (sentDay === null || validUntil === null || asOf === null || asOf === undefined) return null;
  const total = daysBetween(sentDay, validUntil);
  if (total <= 0) return null;
  const ran = Math.min(total, Math.max(0, daysBetween(sentDay, asOf)));
  return { leftPct: wholePercent(total - ran, total), daysLeft: total - ran };
}

export function OfferPanel(props: {
  readonly read: FileOfferRead;
  readonly banksReached: number | null;
  /** `C-5` (N-4): what stands directly under the status line, before the offer's body (the committee stage's card). */
  readonly after?: ReactElement;
}): ReactElement {
  const { read } = props;
  const offer = read.offer;
  const sentOn = read.sent.transmittedAt === null ? null : new Date(read.sent.transmittedAt);
  const validUntil = read.sent.validUntil === null ? null : new Date(`${read.sent.validUntil}T00:00:00Z`);
  const committee = read.requiresCommittee;
  const answer = read.clientAnswer;
  const validity =
    answer === null
      ? validityOf(
          read.sent.transmittedAt === null ? null : read.sent.transmittedAt.slice(0, 10),
          read.sent.validUntil,
          read.asOf,
        )
      : null;
  const guarantees = [
    "bank.offre.garantieDomiciliation",
    "bank.offre.garantieAssurance",
    "bank.offre.garantieGage",
  ] as const;
  return (
    <div data-testid="bank-offre">
      {read.decision === null ? null : <DecisionBanner decision={read.decision} />}
      {/* F5 (a drawing in words): the status line's two states once the client answered ,
          « Offre retenue par le client le <date> » with the circuit that follows, or
          « Offre non retenue par le client, le <date> » with the drawing's note that competing
          offers are never detailed. The expiry chip disappears once he has answered. */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900" data-testid="bank-offre-titre">
            {answer === null
              ? t("bank.offre.attenteReponseClient")
              : answer.kind === "retenue"
                ? fill("bank.offre.retenueLe", { date: formatDate(new Date(answer.at)) })
                : answer.kind === "expiree"
                  ? fill("bank.offre.expireeLe", { date: formatDate(new Date(answer.at)) })
                  : answer.kind === "declinee"
                    ? fill("bank.offre.declineeLe", { date: formatDate(new Date(answer.at)) })
                    : fill("bank.offre.nonRetenueLe", { date: formatDate(new Date(answer.at)) })}
          </p>
          {/* R-D (round `L-2`): the note on competing offers is the context card's, once on the frame. */}
          {answer !== null &&
          (answer.kind === "nonRetenue" || answer.kind === "declinee" || answer.kind === "expiree") ? null : (
            <p className={`mt-0.5 ${BANK_FS_BODY} text-gray-500`} data-testid="bank-offre-statut">
              {answer === null
                ? fill("bank.offre.statutLigne", {
                    sentOn: sentOn === null ? DASH : formatDate(sentOn),
                    validUntil: validUntil === null ? DASH : formatDate(validUntil),
                  })
                : answer.circuit === "comite"
                  ? t("bank.offre.passageComite")
                  : answer.circuit === "signature"
                    ? t("bank.offre.passageDirect")
                    : t("bank.offre.circuitSansSeuil")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* F7: « Expirée » where « Expire le <date> » stood, once the clock has ended it. */}
          {answer !== null && answer.kind === "expiree" ? (
            <span data-testid="bank-offre-expire" data-kind="chip-label">
              <Chip tone="neutre">{t("bank.offre.expiree")}</Chip>
            </span>
          ) : answer !== null ? null : (
            <>
              {validity === null ? null : (
                <ProgressRing label={t("bank.offre.validiteAnneau")} value={validity.leftPct} size={64}>
                  <span className={`${BANK_FS_META} font-semibold tabular-nums`}>
                    {fill("bank.offre.joursRestants", { n: String(validity.daysLeft) })}
                  </span>
                </ProgressRing>
              )}
              <span data-testid="bank-offre-expire" data-kind="chip-label">
                <Chip tone="attention">
                  {validUntil === null ? DASH : fill("bank.offre.expireLe", { date: formatDate(validUntil) })}
                </Chip>
              </span>
            </>
          )}
        </div>
      </div>

      {props.after ?? null}

      <div className="mb-4 grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-4">
          <div
            className="overflow-hidden rounded-2xl border border-gray-200"
            data-testid="bank-offre-transmise"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h3 className="text-sm font-semibold">{t("bank.offre.transmise")}</h3>
              <span className={`${BANK_FS_BODY} text-gray-500`}>{t("bank.offre.nonContractuelle")}</span>
            </div>
            {offer === null ? (
              <p className="px-5 py-4 text-sm text-gray-500">{DASH}</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-x-8 px-5 py-4">
                  <div>
                    <Row
                      label={t("bank.offre.montantFinance")}
                      value={formatMoney(offer.recap.totalFinancedAmount)}
                      strong
                    />
                    <Row
                      label={t("bank.offre.apportClient")}
                      value={
                        read.facts.depositPct === null
                          ? DASH
                          : fill("bank.decision.pct", { n: String(read.facts.depositPct) })
                      }
                    />
                    <Row
                      label={t("bank.decision.duree")}
                      value={fill("bank.composer.mois", { n: String(offer.durationMonths) })}
                    />
                    <Row
                      label={t("bank.composer.fraisBanqueLigne")}
                      value={formatMoney(offer.recap.bankFeeAmount)}
                      testId="bank-offre-frais-banque"
                    />
                  </div>
                  <div>
                    <Row label={t("bank.offre.tauxNominal")} value={pct(offer.nominalRatePct)} />
                    <Row
                      label={t("bank.decision.mensualite")}
                      value={formatMoney(offer.recap.instalmentAmount)}
                      strong
                    />
                    <Row label={t("bank.offre.taeg")} value={pct(offer.recap.taegPct)} />
                    <Row
                      label={t("bank.composer.fraisHakiliLigne")}
                      value={formatMoney(offer.recap.hakiliFeeAmount)}
                      testId="bank-offre-frais-hakili"
                    />
                  </div>
                </div>
                {/* Round `L-3`, `M0 (b)`: the caption follows the OFFER's own flags (03 WF-BNK-11 step 2 in three states). */}
                <p className="-mt-2 px-5 pb-2 text-sm text-gray-400" data-testid="bank-offre-frais-note">
                  {offer.financesBankFee && offer.financesHakiliFee
                    ? t("bank.offre.fraisFinancesTous")
                    : offer.financesBankFee
                      ? t("bank.offre.fraisFinancesBanque")
                      : offer.financesHakiliFee
                        ? t("bank.offre.fraisFinancesHakili")
                        : t("bank.composer.fraisSignatureNote")}
                </p>
                <div className="flex flex-wrap gap-2 px-5 pb-4">
                  {guarantees.map((key) => (
                    <span key={key} data-kind="chip-label">
                      <Chip tone="neutre">{t(key)}</Chip>
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="rounded-2xl border border-gray-200 px-5 py-4" data-testid="bank-offre-circuit">
            <h3 className="mb-2 text-sm font-semibold">{t("bank.offre.circuitApres")}</h3>
            <p className={`whitespace-nowrap ${BANK_FS_BODY}`} data-testid="bank-offre-seuil-ligne">
              <span className="font-medium text-gray-600">{t("bank.composer.seuil")}</span>{" "}
              <span className="tabular-nums text-gray-900" data-testid="bank-offre-seuil">
                {read.facts.delegationThresholdAmount === null
                  ? DASH
                  : formatMoney(read.facts.delegationThresholdAmount)}
              </span>
            </p>
            <p className="mt-2 text-sm leading-relaxed text-gray-700" data-testid="bank-offre-circuit-phrase">
              {committee ? t("bank.offre.circuitComite") : t("bank.offre.circuitDirect")}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-200 px-5 py-4" data-testid="bank-offre-contexte">
            <h3 className="mb-3 text-sm font-semibold">{t("bank.offre.contexte")}</h3>
            <p className="mb-3 text-sm leading-relaxed text-gray-700">
              {props.banksReached === null
                ? t("bank.offre.contexteInconnu")
                : props.banksReached === 1
                  ? t("bank.offre.contexteLigneUn")
                  : fill("bank.offre.contexteLigne", { n: String(props.banksReached) })}
            </p>
            <p className="text-sm leading-relaxed text-gray-400" data-testid="bank-offre-contexte-note">
              {t("bank.offre.contexteNote")}
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 px-5 py-4" data-testid="bank-offre-echeancier">
            <h3 className="mb-3 text-sm font-semibold">{t("bank.offre.echeancier")}</h3>
            <Row
              label={t("bank.offre.expiration")}
              value={validUntil === null ? DASH : formatDate(validUntil)}
            />
            <p className="mt-2 text-sm leading-relaxed text-gray-400">{t("bank.offre.echeancierNote")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Round `L-2`, `M3` (F5): **the stage after the buyer's choice, rendered unbuilt**
 * , the stage's own word for it (domain-types' vocabulary, the drawing's chip
 * , « Au comité », « Signature », « Offre déclinée »), its round in its title (R81). The committee and the signature are `L-3`'s; a lost
 * file's panel is the drawing's « perdu ».
 */
/**
 * Round `L-4`, `M1` and `M2` (F7, a drawing in words): **the file at `perdu`, the
 * banner in four copies by the reason on the row** , declined by the client,
 * not retained, expired by the clock, withdrawn with the buyer's file. `E-C` (D-1019-2, DX-011): the drawing's « Rouvrir »
 * is not drawn , no door reopens a lost file for the bank, and a greyed control with no door is not a control.
 */
/** `L-5`, `M3` (F7 extended): the header's chip at `perdu` reads the row's reason; the stage's one word only when the row carries none. */
export const perduChipWords = (lost: { readonly reason: string; readonly at: string } | null): string =>
  lost === null
    ? BANK_FILE_STAGE_LABELS.perdu
    : lost.reason === "expired"
      ? t("bank.perdu.chipExpiree")
      : lost.reason === "client-declined"
        ? t("bank.perdu.chipDeclinee")
        : lost.reason === "withdrawn"
          ? t("bank.perdu.chipRetiree")
          : t("bank.perdu.chipNonRetenue");

export function LaterStagePanel({ stage }: { readonly stage: string }): ReactElement {
  // Round `C-5` (R-1): a plain card that says where the dossier is. No promise, no dashed placeholder, no round named , and no
  // button, because the stage gives the analyst nothing to do from here.
  return (
    <div className="mb-4 rounded-2xl border border-gray-200 px-5 py-4" data-testid="bank-etape-suivante">
      <p className="text-sm text-gray-700">
        {fill("bank.etape.enMots", { etape: bankFileStepLabel(stage) })}
      </p>
    </div>
  );
}

const bankFileStepLabel = (stage: string): string =>
  (BANK_FILE_STAGE_LABELS as Readonly<Record<string, string>>)[stage] ?? stage;
